<template>
	<DataTable
		:value="exportStatesWithNames"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		columnResizeMode="fit"
		@page="onPage($event)"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:rows="paginationInfo.pageSize"
		:totalRecords="paginationInfo.total"
		:first="(paginationInfo.page || 0) * (paginationInfo.pageSize || 25)"
		:lazy="true"
		:paginator="true"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords} '"
		:autoLayout="true"
		:scrollable="true"
		scrollHeight="flex"
		v-model:filters="filters"
		@filter="onFilter($event)"
		@sort="onSort($event)"
		filterDisplay="row"
		:removableSort="true"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.DocumentClasses.ExportStates')}) }}
		</template>
		<Column field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 6rem; max-width: 6rem;">
			<template #body="slotProps">
				{{ slotProps.data.id }}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="documentId" :header="$t('Squeeze.DocumentClasses.DocumentId')" filterField="documentId" :sortable="true" :showFilterMenu="false" style="min-width: 9rem; max-width: 9rem;">
			<template #body="slotProps">
				{{ slotProps.data.documentId }}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 6rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="interfaceId" :header="$t('Squeeze.DocumentClasses.ExportInterface')" filterField="interfaceName" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getNameFromExportInterface(slotProps.data.interfaceId)">
					{{ getNameFromExportInterface(slotProps.data.interfaceId) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 9rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="status" :header="$t('Squeeze.DocumentClasses.Status')" filterField="status" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; max-width: 15rem;">
			<template #body="slotProps">
				<div class="column-cell p-py-1" :class="'status-badge ' + slotProps.data.status.toLowerCase()">
					{{$t("Squeeze.DocumentClasses.States." + slotProps.data.status)}}
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<Dropdown v-model="filterModel.value" @change="filterCallback()" :options="statuses" placeholder="" class="p-column-filter filter-field" style="height: 2.111rem;" :showClear="true">
					<template #value="slotProps">
						<span v-if="slotProps.value">{{ $t("Squeeze.DocumentClasses.States." + slotProps.value.toUpperCase()) }}</span>
						<span v-else>{{ slotProps.placeholder }}</span>
					</template>
					<template #option="slotProps">
						<span :class="'status-badge ' + slotProps.option.toLowerCase()">{{ $t("Squeeze.DocumentClasses.States." + slotProps.option) }}</span>
					</template>
				</Dropdown>
			</template>
		</Column>
		<Column field="result" :header="$t('Squeeze.DocumentClasses.Result')" filterField="result" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.result">
					{{ slotProps.data.result }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 9rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="timeStamp" :header="$t('Squeeze.DocumentClasses.createdTs')" filterField="timeStampDate" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div style="white-space:nowrap!important;">
					{{ slotProps.data.formattedTimeStamp }}
				</div>
			</template>
			<template #filter="{filterModel,filterCallback}">
				<Calendar
					v-model="filterModel.value"
					showIcon
					showButtonBar
					iconDisplay="input"
					style="min-width: 10rem;"
					class="p-column-filter filter-field"
					:manualInput="false"
					@keydown.enter="filterCallback()"
					@date-select="filterCallback()"
					@clear-click="filterCallback()"
				/>
			</template>
		</Column>

		<template #paginatorstart></template>
		<template #paginatorend></template>
	</DataTable>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref} from "vue";
import {ExportInterfaceDto, PaginationDto} from "@dex/squeeze-client-ts";
import Dropdown from "primevue/dropdown";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Calendar from 'primevue/calendar';
import InputText from "primevue/inputtext";
import {ExportStatus} from "@dex/squeeze-client-ts/dist/api";
import {useI18n} from "vue-i18n";
import {formatDate as formatDateUtil} from "@/util/DateHelper";
import Tooltip from "primevue/tooltip";
import { DocumentFilterObject } from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";

export default defineComponent({
	name: 'DocumentClassExportStates',
	components: {
		Dropdown,
		DataTable,
		Column,
		Calendar,
		InputText,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All export states */
		exportStates: {
			type: Array as PropType<ExportStatus[]>,
			default: () => [],
		},
		paginationInfo: {
			type: Object as PropType<PaginationDto>,
		},
		allExportInterfaces: {
			type: Array as PropType<ExportInterfaceDto[]>,
			default: () => [],
			required: true,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onPage",
		"onFilter",
		"onSort",
	],
	setup(props, {emit}) {
		const {t, locale} = useI18n();

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** ExportStates for filter selection */
		const statuses =  ref<string[]>([
			'ERROR', 'FINISHED', 'EXPORTED', 'ASYNC_WAITING', 'DELETED_BEFORE_EXPORTED', 'MOVED_BEFORE_EXPORTED',
		]);

		/** Triggered when the next page is selected */
		const onPage = (event: PaginationDto) => {
			emit("onPage", event);
		}

		/**
		 * Gets the Name of an export interface
		 * @param interfaceId Id of the export interface to get the name
		 * @return Name of the export Interface
		 */
		const getNameFromExportInterface = (interfaceId: number) => {
			if (interfaceId == null) {
				return t('Squeeze.General.None');
			}
			const exportInterface = props.allExportInterfaces.find(i => i.id === interfaceId);

			if (!exportInterface) {
				return String(interfaceId)
			}
			return exportInterface.interfaceName
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			emit("onFilter", event.filters);
		}

		/**
		 * Triggered when a column sort is changed
		 * @param event
		 */
		const onSort = (event: { sortField: string; sortOrder: number }) => {
			emit("onSort", event.sortField, event.sortOrder);
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"documentId": {value: null, matchMode: FilterMatchMode.EQUALS},
				"interfaceName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"status": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"result": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"timeStampDate": {value: null, matchMode: FilterMatchMode.DATE_IS},
			};
		}

		initFilters();

		/** Export states enriched with a resolved interfaceName for name-based filtering */
		const exportStatesWithNames = computed(() =>
			props.exportStates.map(state => ({
				...state,
				interfaceName: getNameFromExportInterface(state.interfaceId!),
				formattedTimeStamp: formatDateUtil(state.timeStamp!, locale.value),
				timeStampDate: state.timeStamp ? new Date(state.timeStamp) : null,
			}))
		);

		return {
			t,
			filters,
			exportStatesWithNames,
			statuses,
			onPage,
			getNameFromExportInterface,
			onFilter,
			onSort,
		}
	},
})
</script>

<style scoped>

.p-datatable ::v-deep(.p-datatable-thead) {
	z-index: 2;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

.p-datatable ::v-deep(.p-datatable-thead .p-dropdown.filter-field) {
	padding:0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

.p-datatable ::v-deep(.p-datatable-thead .p-dropdown.filter-field .p-dropdown-label) {
	padding: 0 1rem 0 0.4rem;
	color: var(--filter-field-color);
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

::v-deep(.p-calendar > .p-inputtext) {
	padding: 0.4rem;
	color: var(--dex-text-color);
	border-color: var(--dex-text-color) !important;
	background-color: transparent;
	border-radius: 0;
}

::v-deep(svg.p-icon.p-datepicker-trigger-icon.p-input-icon) {
	color: var(--dex-text-color);
	opacity: 0.6;
}

::v-deep(.p-calendar > .p-datepicker-trigger.p-button) {
	padding: 0;
}

.status-badge {
	font-weight: bold;
	&.exported {
		border-color: var(--queuestate-initial-primary);
		color: var(--queuestate-initial-darker);
	}

	&.moved_before_exported {
		border-color: var(--queuestate-work-primary);
		color: var(--queuestate-work-darker);
	}

	&.async_waiting {
		border-color: var(--queuestate-waiting-primary);
		color: var(--queuestate-waiting-darker);
	}

	&.error {
		border-color: var(--queuestate-error-primary);
		color: var(--queuestate-error-darker);
	}

	&.delete_before_exported {
		border-color: var(--queuestate-clarification-primary);
		color: var(--queuestate-clarification-darker);
	}

	&.finished {
		border-color: var(--queuestate-finished-primary);
		color: var(--queuestate-finished-darker);
	}
}

</style>
